import React, { useEffect, useMemo, useRef, useState } from "react";
import { SAFE_BOTTOM } from "../../styles/safeArea";
import { useNavigate, useLocation } from "react-router-dom";
import { BOTTOM_NAV_ITEMS, isBottomNavRoute } from "../navConfig";
import { MOTION } from "../../styles/motionTokens";
import { prefersReducedMotion } from "../../styles/particleField";
import { Z } from "../../styles/zLayers";
import { subscribeTradeMatches } from "../../trades/tradeMatchSignal";
import { useTradeActions } from "../../trades/providers/TradeActionProvider";
import { useNotifications } from "../../notifications/providers/NotificationProvider";
import { useUser } from "../../users/providers/UserProvider";
import { tradesBadgeCount } from "../../trades/tradeUiLogic";
import { useKeyboardInset } from "../../native/keyboardInset";

/**
 * B-70: a second tap on the tab you are on goes back to the top, as phone users
 * expect. Every scrolled element under the shell is reset, not just the shell's
 * own scrollport, because some tabs (the chat list) scroll an inner box.
 * Offsets are read first and written after, so the reads never wait on layout.
 */
export const scrollShellToTop = (root) => {
  if (!root) return;
  [root, ...root.querySelectorAll("*")]
    .filter((el) => el.scrollTop > 0)
    .forEach((el) => {
      el.scrollTop = 0;
    });
};

const ICONS = {
  collection: (
    <svg width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
    </svg>
  ),
  map: (
    <svg width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
    </svg>
  ),
  trades: (
    <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 7h12m0 0l-4-4m4 4l-4 4M16 17H4m0 0l4 4m-4-4l4-4" />
    </svg>
  ),
  wishlist: (
    <svg width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
    </svg>
  ),
  chat: (
    <svg width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  ),
};

const Footer = ({ scrollEl }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { notifications, unreadByType, unreadTradeIds } = useNotifications();
  const { pendingActionIds } = useTradeActions();
  const { user } = useUser();

  // The server's per-type unread total, when it sends one. Counting the
  // `notifications` array instead is what BUG #29 was: that array is a single
  // page (30 rows), so an unread chat notification older than the 30 newest
  // notifications is simply absent from it and the badge reads low — 0 once
  // every unread chat has been pushed off the page, while the conversation is
  // still waiting on the user.
  //
  // The fallback is not politeness: a client on an older server, or replaying a
  // cached response from before the field existed, gets `null` here, and an
  // undercounted badge beats no badge at all. `unreadCount` cannot substitute —
  // it spans every notification type, so one unread chat behind thirty trade
  // notifications would put 31 on the chat tab.
  const chatUnread = useMemo(
    () =>
      unreadByType
        ? unreadByType.chat_message || 0
        : notifications.filter((n) => n.type === "chat_message" && !n.read).length,
    [unreadByType, notifications]
  );

  // Trades: distinct trades that are waiting on you OR carry an update you
  // have not opened (P0 area C — see tradesBadgeCount).
  const tradeActionCount = useMemo(
    () => tradesBadgeCount(pendingActionIds, notifications, unreadTradeIds),
    [pendingActionIds, notifications, unreadTradeIds]
  );

  // Chat is the only tab that can badge: a badge means someone is waiting on
  // you. Inventory counts live on the pages, not the nav (see navConfig).
  const badgeFor = (badge) => (badge === "chat" ? chatUnread : 0);

  // The Trades hero acknowledges a NEW match with a single wobble, then goes
  // still. Never an idle loop — a permanently animating primary action is the
  // motion equivalent of the inventory badges we just removed.
  const [wobbling, setWobbling] = useState(false);
  const wobbleTimer = useRef(null);

  useEffect(() => {
    const unsubscribe = subscribeTradeMatches(() => {
      if (prefersReducedMotion()) return;
      setWobbling(true);
      clearTimeout(wobbleTimer.current);
      wobbleTimer.current = setTimeout(
        () => setWobbling(false),
        MOTION.tradeWobbleMs
      );
    });
    return () => {
      unsubscribe();
      clearTimeout(wobbleTimer.current);
    };
  }, []);

  // No session, no nav (#76). Every one of BOTTOM_NAV_ITEMS routes to a
  // RequireAuth destination, and RequireAuth sends a null user to /login — so
  // for a signed-out visitor these five tabs are five buttons that can only
  // bounce them to the login page.
  //
  // Layout decides chrome by ROUTE (chromeRoutes.js `isAuthRoute`), which is
  // right for the auth pathnames it enumerates but cannot cover the cases where
  // "should there be nav here?" is a SESSION fact rather than a route fact:
  // the unguarded catch-all `*` in Router.jsx, which matches any pathname
  // nobody registered; `/` during the frame where RootRedirect returns null
  // because auth has not hydrated; and — a third family a skeptic added after
  // this comment first claimed there were exactly two — any in-shell
  // RequireAuth route (/map, /collection, /trades, …) deep-linked by a
  // signed-out visitor, where RequireAuth redirects in an effect so there is a
  // commit that paints the nav around an empty page first. The rule lives here
  // rather than in the allowlist because it is about the session — the same
  // shape the other two pieces of app chrome already use: TopAccountBar reads
  // `useUser` itself, and OnboardingCoach renders nothing without a userId.
  //
  // Below every hook on purpose: an early return above them would make the
  // hook order depend on the session.
  //
  // B-04: while the keyboard is up the bar rode up with it and covered search
  // results. The native bridge reports the keyboard height (0 when closed, and
  // always 0 on web), so the bar simply steps aside while the user types.
  const keyboardOpen = useKeyboardInset() > 0;
  if (!user || keyboardOpen) return null;

  // Re-tapping the tab you are on scrolls to the top instead of pushing the
  // same page again (B-70). A tab with a query string still goes back to its
  // plain route, as it always did.
  const go = (route) => {
    if (location.pathname === route) {
      scrollShellToTop(scrollEl);
      if (!location.search) return;
    }
    navigate(route);
  };

  return (
    <div
      data-testid="app-footer"
      // tl-footer / tl-footer-pill / tl-nav-hero: hooks for the short-window
      // (split screen, landscape phone) rules in index.css (B-40).
      className="tl-footer"
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: Z.chrome,
        // The 24px used to be the whole bottom padding, which put the nav pill
        // exactly 24px above the display edge on every device. That is fine on
        // a phone with no system bar and wrong everywhere else: android
        // `targetSdkVersion = 36` (android/variables.gradle) enforces
        // edge-to-edge with no opt-out, and index.html asks for
        // `viewport-fit=cover`, so the WebView really does extend under the
        // system navigation. With a 48dp 3-button bar the pill's bottom 24px
        // sat BEHIND it and ~14px of every tab's hit box was unreachable.
        //
        // SAFE_BOTTOM is now IMPORTED from src/styles/safeArea.js rather than
        // hand-copied: this used to be its own literal, and the copy was
        // unguarded — a tidy-up to a bare env() here kept every suite green
        // while reintroducing the Android defect. Custom property
        // first because Capacitor 8 publishes the true insets there and
        // Chromium 140's own env() is wrong under edge-to-edge — inlined
        // rather than imported so app-wide chrome does not depend on the map.
        padding:
          `0 16px calc(24px + ${SAFE_BOTTOM})`,
        background: "linear-gradient(to top, rgba(248,247,250,1) 60%, rgba(248,247,250,0) 100%)",
        pointerEvents: "none",
      }}
    >
      <div
        className="tl-footer-pill"
        style={{
          maxWidth: 480,
          margin: "0 auto",
          background: "rgba(15,13,17,0.96)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          borderRadius: 28,
          boxShadow: "0 8px 32px rgba(0,0,0,0.3), 0 2px 8px rgba(0,0,0,0.2)",
          display: "flex",
          alignItems: "flex-end",
          // No side padding: the five tabs share the whole width (B-48).
          // The top and bottom padding is still outside every tab, so a tap
          // in those strips (and above the four plain tabs, which sit lower
          // than the hero) hits nothing, as it did before B-48.
          padding: "8px 0 10px",
          pointerEvents: "all",
          border: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        <nav
          aria-label="Main"
          style={{ flex: 1, display: "flex", alignItems: "flex-end" }}
        >
          {BOTTOM_NAV_ITEMS.map((item) => {
            const active = isBottomNavRoute(location.pathname, item.route);
            const badgeCount = badgeFor(item.badge);
            const isHero = !!item.hero;

            if (isHero) {
              return (
                <button
                  key={item.id}
                  type="button"
                  data-testid="nav-trades-hero"
                  data-nav-id={item.id}
                  data-active={active ? "true" : "false"}
                  aria-label="Trades"
                  aria-current={active ? "page" : undefined}
                  onClick={() => go(item.route)}
                  className="tl-nav-hero"
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 4,
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "0 4px",
                    marginTop: -18,
                    fontFamily: "Inter, sans-serif",
                    outline: "none",
                    color: "#fff",
                    transition: `transform ${MOTION.sleevePressMs}ms ${"cubic-bezier(0.4, 0, 0.2, 1)"}`,
                  }}
                  onMouseDown={(e) => {
                    e.currentTarget.style.transform = `scale(${MOTION.pressScale})`;
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                  }}
                >
                  {/* Separate relative wrapper — the pill itself clips (overflow:
                      hidden, for the wobble) so a corner badge has to live outside it. */}
                  <div style={{ position: "relative" }}>
                    <span
                      className="nav-trades-hero-pill"
                      data-testid="nav-trades-pill"
                      data-wobbling={wobbling ? "true" : "false"}
                      style={{
                        width: 56,
                        height: 56,
                        borderRadius: 18,
                        background: active
                          ? "linear-gradient(145deg, #7c3aed 0%, #6d28d9 100%)"
                          : "linear-gradient(145deg, #8b5cf6 0%, #6d28d9 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: active
                          ? "0 8px 24px rgba(109,40,217,0.55), 0 0 0 2px rgba(255,255,255,0.12)"
                          : "0 6px 18px rgba(109,40,217,0.4)",
                        position: "relative",
                        overflow: "hidden",
                        // perspective lives on the element so rotateY reads as a
                        // physical tilt rather than a flat horizontal squash.
                        perspective: 220,
                        transformStyle: "preserve-3d",
                        animation: wobbling
                          ? `tradeHeroWobble ${MOTION.tradeWobbleMs}ms cubic-bezier(0.36, 0.07, 0.19, 0.97) both`
                          : "none",
                      }}
                    >
                      {ICONS.trades}
                    </span>
                    {tradeActionCount > 0 && (
                      <span
                        data-testid="nav-badge-trades"
                        style={{
                          position: "absolute",
                          top: -4,
                          right: -6,
                          background: "#7c3aed",
                          color: "#fff",
                          fontSize: 9,
                          fontWeight: 800,
                          borderRadius: 999,
                          minWidth: 16,
                          height: 16,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "0 3px",
                          border: "2px solid rgba(15,13,17,0.96)",
                        }}
                      >
                        {tradeActionCount > 99 ? "99+" : tradeActionCount}
                      </span>
                    )}
                  </div>
                  <span className="tl-nav-label" style={{ fontSize: 9, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    {item.label}
                  </span>
                </button>
              );
            }

            return (
              <button
                key={item.id}
                type="button"
                data-testid={`nav-${item.id}`}
                data-nav-id={item.id}
                data-active={active ? "true" : "false"}
                aria-label={item.label}
                aria-current={active ? "page" : undefined}
                onClick={() => go(item.route)}
                style={{
                  // B-48: each tab fills its fifth of the bar, so there is no
                  // dead gap between tabs and no target under 48px. Sized from
                  // content, Map was 31px wide and Chat 35px.
                  flex: 1,
                  minWidth: 48,
                  minHeight: 48,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 3,
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "6px 4px",
                  borderRadius: 12,
                  color: active ? "#a855f7" : "#64748b",
                  transition: `color ${MOTION.pressMs}ms ease, transform ${MOTION.pressMs}ms ease`,
                  fontFamily: "Inter, sans-serif",
                  outline: "none",
                  position: "relative",
                }}
              >
                <div style={{ opacity: active ? 1 : 0.75, position: "relative" }}>
                  {ICONS[item.id]}
                  {badgeCount > 0 && (
                    <span
                      data-testid={`nav-badge-${item.id}`}
                      style={{
                        position: "absolute",
                        top: -4,
                        right: -6,
                        background: "#7c3aed",
                        color: "#fff",
                        fontSize: 9,
                        fontWeight: 800,
                        borderRadius: 999,
                        minWidth: 16,
                        height: 16,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "0 3px",
                        border: "2px solid rgba(15,13,17,0.96)",
                      }}
                    >
                      {badgeCount > 99 ? "99+" : badgeCount}
                    </span>
                  )}
                </div>
                <span className="tl-nav-label" style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.02em" }}>
                  {item.label}
                </span>
              </button>
            );
          })}
        </nav>
      </div>

      <style>{`
        /* Decaying side-to-side tilt — settles rather than stopping dead. */
        @keyframes tradeHeroWobble {
          0%   { transform: rotateY(0deg)   rotateZ(0deg); }
          18%  { transform: rotateY(26deg)  rotateZ(3deg); }
          38%  { transform: rotateY(-22deg) rotateZ(-2.5deg); }
          56%  { transform: rotateY(15deg)  rotateZ(1.6deg); }
          72%  { transform: rotateY(-9deg)  rotateZ(-1deg); }
          87%  { transform: rotateY(4deg)   rotateZ(0.4deg); }
          100% { transform: rotateY(0deg)   rotateZ(0deg); }
        }
        @media (prefers-reduced-motion: reduce) {
          .nav-trades-hero-pill { animation: none !important; }
        }
      `}</style>
    </div>
  );
};

export default React.memo(Footer);
